import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  IconButton,
  Menu,
  ListItemIcon,
  Divider,
  AppBar,
  Toolbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Snackbar,
  Tooltip,
  Fab,
  TextField,
  InputAdornment,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Edit as EditIcon,
  CloudDownload as ImportIcon,
  Publish as PublishIcon,
  Delete as DeleteIcon,
  DoNotDisturb as DeprecateIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  ContentCopy as DuplicateIcon,
  Search as SearchIcon,
  DriveFileMoveOutlined as MoveIcon,
  MoreVert as MoreIcon,
  Block as BlockIcon,
  Speed as SpeedIcon,
  Sync as SyncIcon,
  ErrorOutline as ErrorOutlineIcon,
  EditOutlined as DraftIcon,
  Bolt as BoltIcon,
  Forum as ForumIcon
} from '@mui/icons-material';
import { getFlowStatusDisplay, getFlowErrorCount, isFlowInvalid, getFlowErrorMessages } from '../../utils/flowStatus';
import { track, flowProps } from '../../utils/analytics';
import { useFlow } from '../../contexts/FlowContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useWhatsAppConnection } from '../../contexts/WhatsAppConnectionContext';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import ImportFlowsDialog from '../../components/flows/dialogs/ImportFlowsDialog';
import TemplateGalleryDialog from '../../components/flows/dialogs/TemplateGalleryDialog';
import DynamicFlowChip from '../../components/flows/DynamicFlowChip';
import LiveBookingChip from '../../components/flows/LiveBookingChip';
import { isBookingFlowJson } from '../../flow-core/booking';
import { flowService } from '../../services/flowService';
import welcomeChipsService from '../../services/welcomeChipsService';
import { SOURCE_LABELS } from '../../components/FlowBuilder/FlowTriggerDialog';
import FlowPhonePreview from '../../components/flows/FlowPhonePreview';
import HubIcon from '@mui/icons-material/HubOutlined';
import DestinationDialog from '../../components/deliveries/DestinationDialog';
import FlowConnectionsDrawer from '../../components/flows/FlowConnectionsDrawer';
import { iconFor as connectorIconFor } from '../../components/deliveries/connectorIcons';
import { deliveryService } from '../../services/deliveryService';
import ConnectWabaDialog from '../../components/environment/dialogs/ConnectWabaDialog';

// Icon per lifecycle status key returned by getFlowStatusDisplay.
const STATUS_ICONS = {
  published: <PublishIcon />,
  draft: <DraftIcon />,
  deprecated: <DeprecateIcon />,
  blocked: <BlockIcon />,
  throttled: <SpeedIcon />,
  syncing: <SyncIcon />,
  'sync-failed': <ErrorOutlineIcon />,
};

const FlowsPage = () => {
  const {
    flows,
    loadFlowForEditing,
    deleteFlow,
    deployToWhatsApp,
    publishFlow,
    deprecateFlow,
    duplicateFlow,
    moveFlow,
    isLoading,
    hasLoadedFlows,
    error
  } = useFlow();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { isFree, limits } = useEntitlements();
  const { isDemoMode, authenticatedFetch } = useAuth();
  const { environments, selectedEnvironment } = useEnvironment();
  const { isConnected } = useWhatsAppConnection();
  const otherEnvironments = (environments || []).filter(
    (e) => e.id !== selectedEnvironment?.id && !e.isArchived
  );
  // A phone is not a narrow desktop: the toolbar's four ways to start a flow become one menu,
  // the card lays its actions out under the preview instead of beside it, and the date loses
  // the year nobody was reading. Everything still on screen, nothing off the right edge.
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper function for date formatting
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // On a phone the card has room for "20 Sep", not "Sep 20, 2026, 05:28 PM", which wrapped to
  // four lines and told the owner of a flow they updated an hour ago nothing they wanted.
  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
  };

  // Local state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, flow: null });
  const [deprecateDialog, setDeprecateDialog] = useState({ open: false, flow: null });
  const [editPublishedDialog, setEditPublishedDialog] = useState({ open: false, flow: null });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deliveryDialogFlow, setDeliveryDialogFlow] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const toggleSelect = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const clearSelection = () => setSelectedIds([]);

  // Auto-send triggers, grouped by flow id, shown as chips on the flow cards. One env-wide
  // fetch (not per card); refreshed when the environment or the flow list changes.
  const [triggersByFlow, setTriggersByFlow] = useState({});
  const [triggersRefresh, setTriggersRefresh] = useState(0);
  useEffect(() => {
    if (!selectedEnvironment?.id) {
      setTriggersByFlow({});
      return;
    }
    let cancelled = false;
    flowService.listEnvironmentTriggers(selectedEnvironment.id, authenticatedFetch)
      .then((list) => {
        if (cancelled) return;
        const grouped = {};
        (list || []).forEach((t) => {
          (grouped[t.flowId] = grouped[t.flowId] || []).push(t);
        });
        setTriggersByFlow(grouped);
      })
      .catch(() => { if (!cancelled) setTriggersByFlow({}); });
    return () => { cancelled = true; };
    // authenticatedFetch is recreated every AuthContext render - depending on it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvironment?.id, flows.length, triggersRefresh]);

  // Where each flow's answers actually land, shown on its card, a flow nobody has pointed
  // anywhere is a flow quietly collecting submissions the owner never sees. One environment-wide
  // request whatever the flow count: the server resolves the flow's own destinations against the
  // environment defaults, so a flow absent from `flows` here simply delivers to `defaults`.
  const [destinations, setDestinations] = useState({ defaults: [], flows: {} });
  const [destinationsRefresh, setDestinationsRefresh] = useState(0);
  // The flow whose Connections panel is open: keywords, live data and where answers go.
  const [connectionsFlow, setConnectionsFlow] = useState(null);
  useEffect(() => {
    if (!selectedEnvironment?.id) {
      setDestinations({ defaults: [], flows: {} });
      return;
    }
    let cancelled = false;
    deliveryService.destinationSummary(selectedEnvironment.id, authenticatedFetch)
      .then((summary) => {
        if (!cancelled) {
          setDestinations({ defaults: summary?.defaults || [], flows: summary?.flows || {} });
        }
      })
      // A card that cannot say where answers go says nothing, rather than "nowhere".
      .catch(() => { if (!cancelled) setDestinations(null); });
    return () => { cancelled = true; };
    // authenticatedFetch is recreated every AuthContext render - depending on it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvironment?.id, flows.length, destinationsRefresh]);

  /** What this flow delivers to: its own destinations if it has any, otherwise the environment's. */
  const destinationsFor = (flowId) => {
    if (!destinations) return null;
    return destinations.flows[flowId] || destinations.defaults;
  };

  // First run with nothing built yet: straight into the assistant, where the first thing on
  // screen is the box that asks what the flow should do. It used to open a dental booking
  // template instead, which answered a question the visitor had not been asked, and told
  // somebody who had just described their own business that we had something else in mind.
  useEffect(() => {
    if (!isDemoMode || !hasLoadedFlows || isLoading || flows.length > 0) return;
    if (sessionStorage.getItem('fg_first_run_done')) return;
    sessionStorage.setItem('fg_first_run_done', '1');
    track('first_run_builder_opened');
    navigate('/flow-builder?new=1&mode=assistant');
  }, [isDemoMode, hasLoadedFlows, isLoading, flows.length, navigate]);

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    let deleted = 0;
    let keptOnWhatsApp = 0;
    let skipped = 0;
    for (const id of ids) {
      const flow = flows.find((f) => f.id === id);
      if (flow?.status === 'PUBLISHED') { skipped += 1; continue; }
      try {
        const res = await deleteFlow(id);
        deleted += 1;
        if (res && res.removedFromWhatsApp === false) keptOnWhatsApp += 1;
      } catch (err) { /* keep going */ }
    }
    setBulkDeleteOpen(false);
    clearSelection();
    const parts = [];
    if (skipped) parts.push(`skipped ${skipped} published`);
    if (keptOnWhatsApp) parts.push(`${keptOnWhatsApp} kept in WhatsApp (already published)`);
    setSnackbar({
      open: true,
      severity: deleted ? 'success' : 'info',
      message: `Removed ${deleted} flow${deleted === 1 ? '' : 's'} from FlowGate${parts.length ? `, ${parts.join(', ')}` : ''}`,
    });
  };

  const filteredFlows = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return flows;
    return flows.filter(f =>
      (f.name || '').toLowerCase().includes(q) || (f.description || '').toLowerCase().includes(q)
    );
  }, [flows, searchQuery]);

  const handlePickTemplate = (template) => {
    setTemplateDialogOpen(false);
    navigate(`/flow-builder?template=${encodeURIComponent(template.id)}`);
  };

  // Individual flow delete handler
  const handleDeleteFlow = async (flow) => {
    try {
      const res = await deleteFlow(flow.id);
      track('flow_deleted', { ...flowProps(flow), removedFromWhatsApp: res?.removedFromWhatsApp !== false });
      setDeleteDialog({ open: false, flow: null });
      // WhatsApp keeps published flows permanently, be honest when we could only archive locally.
      const keptOnWhatsApp = res && res.removedFromWhatsApp === false && flow.whatsappFlowId;
      setSnackbar({
        open: true,
        severity: keptOnWhatsApp ? 'info' : 'success',
        message: keptOnWhatsApp
          ? 'Removed from FlowGate. WhatsApp keeps published flows permanently, so it remains in your WABA as Deprecated.'
          : 'Flow deleted from FlowGate and WhatsApp.',
      });
    } catch (error) {
      console.error('Error deleting flow:', error);
      setSnackbar({ open: true, message: `Delete failed: ${error.message}`, severity: 'error' });
    }
  };

  const [moveDialog, setMoveDialog] = useState({ open: false, flow: null, target: '' });

  const handleMoveFlow = async () => {
    const { flow, target } = moveDialog;
    if (!target) return;
    try {
      await moveFlow(flow.id, target);
      setMoveDialog({ open: false, flow: null, target: '' });
      const envName = otherEnvironments.find((e) => e.id === target)?.name || 'another environment';
      setSnackbar({ open: true, message: `Moved "${flow.name}" to ${envName}`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Move failed: ${err.message}`, severity: 'error' });
    }
  };

  // Duplicate a flow into a new "(copy)" within the same environment.
  const handleDuplicateFlow = async (flow) => {
    try {
      const copy = await duplicateFlow(flow.id);
      track('flow_duplicated', flowProps(flow));
      setSnackbar({ open: true, message: `Duplicated as "${copy.name}"`, severity: 'success' });
    } catch (error) {
      console.error('Error duplicating flow:', error);
      setSnackbar({ open: true, message: `Duplicate failed: ${error.message}`, severity: 'error' });
    }
  };

  const openFlowInBuilder = (flowId) => {
    loadFlowForEditing(flowId);
    navigate(`/flow-builder/${flowId}`);
  };

  const handleEditFlow = async (flow) => {
    // Editing a published flow unpublishes it on WhatsApp (Meta reverts it to draft). Warn first and
    // offer to edit a copy so the live flow stays published.
    if (flow.status === 'PUBLISHED' || flow.whatsappStatus === 'PUBLISHED') {
      setEditPublishedDialog({ open: true, flow });
      return;
    }
    openFlowInBuilder(flow.id);
  };

  const handleEditPublishedAsCopy = async () => {
    const flow = editPublishedDialog.flow;
    setEditPublishedDialog({ open: false, flow: null });
    if (!flow) return;
    try {
      const copy = await duplicateFlow(flow.id);
      setSnackbar({ open: true, severity: 'success', message: `Editing a copy "${copy.name}", the live flow stays published.` });
      openFlowInBuilder(copy.id);
    } catch (error) {
      console.error('Error creating copy to edit:', error);
      setSnackbar({ open: true, severity: 'error', message: `Could not create a copy: ${error.message}` });
    }
  };

  const handleEditPublishedAnyway = () => {
    const flow = editPublishedDialog.flow;
    setEditPublishedDialog({ open: false, flow: null });
    if (flow) openFlowInBuilder(flow.id);
  };

  const handleDeployFlow = async (flowId) => {
    const props = flowProps(flows.find((f) => f.id === flowId) || { id: flowId });
    try {
      await deployToWhatsApp(flowId);
      track('flow_deployed', props);
      setSnackbar({ open: true, message: 'Flow deployed to WhatsApp successfully!', severity: 'success' });
    } catch (error) {
      console.error('Deploy failed:', error);
      track('flow_deploy_failed', { ...props, error: error.message });
      setSnackbar({ open: true, message: `Deploy failed: ${error.message}`, severity: 'error' });
    }
  };

  const handlePublishFlow = async (flowId) => {
    const props = flowProps(flows.find((f) => f.id === flowId) || { id: flowId });
    try {
      await publishFlow(flowId);
      track('flow_published', props);
      setSnackbar({ open: true, message: 'Flow published successfully!', severity: 'success' });
    } catch (error) {
      console.error('Publish failed:', error);
      track('flow_publish_failed', { ...props, error: error.message });
      setSnackbar({ open: true, message: `Publish failed: ${error.message}`, severity: 'error' });
    }
  };

  const handleDeprecateFlow = async (flow) => {
    try {
      await deprecateFlow(flow.id);
      track('flow_deprecated', flowProps(flow));
      setDeprecateDialog({ open: false, flow: null });
      setSnackbar({ open: true, message: 'Flow deprecated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Deprecate failed:', error);
      setSnackbar({ open: true, message: `Deprecate failed: ${error.message}`, severity: 'error' });
    }
  };

  // Custom hook for flow card logic
  const useFlowCard = (flow) => {
    const stats = React.useMemo(() => {
      const screens = flow.flowJson?.screens || [];
      return {
        screens: screens.length,
        components: screens.reduce(
          (total, screen) => total + (screen.layout?.children?.length || 0),
          0
        )
      };
    }, [flow.flowJson]);

    const statusInfo = React.useMemo(() => {
      const display = getFlowStatusDisplay(flow);
      return { ...display, icon: STATUS_ICONS[display.key] };
    }, [flow]);

    const formattedDate = React.useMemo(() => {
      const format = narrow ? formatDateShort : formatDate;
      return {
        created: format(flow.createdAt),
        updated: format(flow.updatedAt),
        deployed: flow.deployedAt ? format(flow.deployedAt) : null
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flow.createdAt, flow.updatedAt, flow.deployedAt, narrow]);

    return { stats, statusInfo, formattedDate };
  };

  /**
   * The toolbar's ways to start a flow (describe, import), folded into one button for a phone.
   * Same actions, same order, same permissions, "Describe your flow" stays first here even though the floating +
   * does the same thing, because a menu that omits the main action reads as if it were missing.
   */
  const FlowsToolbarMenu = () => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const close = () => setAnchorEl(null);
    const go = (fn) => () => { close(); fn(); };

    return (
      <>
        <IconButton
          aria-label="More ways to start a flow"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={isLoading}
          data-testid="flows-toolbar-menu"
        >
          <MoreIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
          {can.modifyFlow && (
            <MenuItem onClick={go(() => navigate('/flow-builder?new=1'))}>
              <ListItemIcon><AutoAwesomeIcon fontSize="small" /></ListItemIcon>
              Describe your flow
            </MenuItem>
          )}
          {can.importFlow && (
            <MenuItem onClick={go(() => setImportDialogOpen(true))}>
              <ListItemIcon><ImportIcon fontSize="small" /></ListItemIcon>
              Import
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const FlowCardActions = ({ flow, can, onEdit, onDuplicate, onMove, onPublish, onDeprecate, onDelete, onDeliverAnswers, onConnections }) => {
    const isPublished = flow.status === 'PUBLISHED';
    const isDeployed = flow.status === 'DEPLOYED';
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const closeMenu = () => setMenuAnchor(null);
    const runAndClose = (fn) => () => { closeMenu(); fn(); };

    return (
      <CardActions sx={{ gridArea: 'actions', px: 1.5, py: 1, gap: 0.5, minWidth: 0 }}>
        {/* Publish is the money action (contained); Edit stays quiet. */}
        {can.modifyFlow && (
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => onEdit(flow)}
            disabled={isLoading}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'rgba(11, 13, 18, 0.05)' } }}
          >
            Edit
          </Button>
        )}

        {can.publishFlow && isDeployed && (
          <Button
            size="small"
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={() => onPublish(flow.id)}
            disabled={isLoading}
          >
            Publish
          </Button>
        )}

        {onConnections && (
          <Button
            size="small"
            startIcon={<HubIcon />}
            onClick={() => onConnections(flow)}
            sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'rgba(11, 13, 18, 0.05)' } }}
            data-testid={`flow-connections-${flow.id}`}
          >
            Connections
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <IconButton
          size="small"
          aria-label={`More actions for ${flow.name}`}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <MoreIcon fontSize="small" />
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {onDeliverAnswers && (
            <MenuItem onClick={runAndClose(() => onDeliverAnswers(flow))} data-testid={`deliver-answers-menu-${flow.id}`}>
              <ListItemIcon><ForumIcon fontSize="small" /></ListItemIcon>
              Deliver answers…
            </MenuItem>
          )}

          {can.modifyFlow && (
            <MenuItem onClick={runAndClose(() => onDuplicate(flow))} disabled={isLoading}>
              <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon>
              Duplicate
            </MenuItem>
          )}

          {can.modifyFlow && onMove && !isPublished && (
            <MenuItem onClick={runAndClose(() => onMove(flow))} disabled={isLoading}>
              <ListItemIcon><MoveIcon fontSize="small" /></ListItemIcon>
              Move to environment
            </MenuItem>
          )}

          {can.modifyFlow && isPublished && (
            <MenuItem onClick={runAndClose(() => onDeprecate(flow))} disabled={isLoading}>
              <ListItemIcon><DeprecateIcon fontSize="small" /></ListItemIcon>
              Deprecate
            </MenuItem>
          )}

          {can.deleteFlow && <Divider />}
          {can.deleteFlow && (
            <MenuItem
              onClick={runAndClose(() => onDelete(flow))}
              disabled={isPublished || isLoading}
              sx={{ color: isPublished ? 'text.disabled' : 'error.main' }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color={isPublished ? 'disabled' : 'error'} />
              </ListItemIcon>
              {isPublished ? 'Delete (deprecate first)' : 'Delete'}
            </MenuItem>
          )}
        </Menu>
      </CardActions>
    );
  };

  /**
   * Where this flow's answers go, on the card that owns them.
   *
   * <p>Every submission is recorded and listed under Submissions whatever happens here, so an
   * empty row is an offer rather than a warning: these are the places an answer is *also* sent.
   * An inherited target says so in its tooltip, because turning it off is done in Settings and
   * not on this flow.
   */
  const FlowDestinations = ({ flow, targets }) => {
    if (!targets) return null;

    // Nothing to offer a demo account: the backend refuses every write under
    // /flows/*/destinations (DemoRestrictionFilter.NOT_FOR_DEMO), because a destination is
    // where somebody else's answers get sent and "test" makes the server send a real one.
    // Offering the button anyway would be a button whose only outcome is a 403.
    if (isDemoMode) return null;

    if (targets.length === 0) {
      return (
        <Box sx={{ mt: 0.75 }}>
          <Chip
            size="small"
            variant="outlined"
            icon={<AddIcon />}
            label={narrow ? 'Send answers' : 'Send answers somewhere'}
            onClick={() => setDeliveryDialogFlow(flow)}
            sx={{ borderStyle: 'dashed', color: 'text.secondary' }}
            data-testid={`flow-destinations-none-${flow.id}`}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
        <Typography variant="caption" color="text.secondary">Answers go to</Typography>
        {targets.map((target) => (
          <Tooltip
            key={target.type}
            title={target.inherited
              ? `${target.displayName}, a default for every flow in this environment`
              : `${target.displayName}, set up on this flow`}
          >
            <Chip
              size="small"
              variant="outlined"
              icon={connectorIconFor(target.type)}
              label={target.displayName}
              onClick={() => setDeliveryDialogFlow(flow)}
              sx={{ maxWidth: '100%' }}
              data-testid={`flow-destination-${flow.id}-${target.type}`}
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  // Memoized flow card component
  const FlowCard = React.memo(({ flow, selected, onToggleSelect, triggers }) => {
    const { stats, statusInfo, formattedDate } = useFlowCard(flow);

    return (
      <Grid item xs={12} lg={6} key={flow.id}>
        {/* Two columns of grid, so the actions can drop underneath the preview on a phone
            instead of sharing 150px of row with it, which is what squeezed "Publish" down to
            "Publis…" and pushed the overflow menu off the right edge of the screen. */}
        <Card sx={{
          height: '100%', position: 'relative', overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr)',
          gridTemplateRows: '1fr auto',
          gridTemplateAreas: {
            xs: '"preview details" "actions actions"',
            sm: '"preview details" "preview actions"',
          },
          outline: selected ? '2px solid' : '1px solid',
          outlineColor: selected ? 'primary.main' : 'divider', outlineOffset: '-1px',
          transition: 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { boxShadow: '0 18px 40px -20px rgba(11, 13, 18, 0.16)', transform: 'translateY(-2px)' },
          '&:hover .flow-card-select': { opacity: 1 },
        }}>
          {/* Miniature phone preview of the flow's first screen, click to open in the builder. */}
          <Box
            sx={{
              gridArea: 'preview',
              p: { xs: 1.25, sm: 2 }, display: 'flex', alignItems: 'center', cursor: 'pointer',
              bgcolor: 'action.hover',
            }}
            onClick={() => handleEditFlow(flow)}
          >
            <FlowPhonePreview flowJson={flow.flowJson} width={narrow ? 116 : 150} />
          </Box>

          {/* Details beside the preview; on a phone the actions run full width underneath. */}
          <CardContent sx={{ gridArea: 'details', minWidth: 0, pb: 1, px: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              {/* A phone's details column is 170px wide: one line turns most names into
                  "Barber shop b…", so give it two before the ellipsis. */}
              <Typography
                variant="subtitle1" component="h2" noWrap={!narrow} title={flow.name}
                sx={{
                  fontWeight: 600, flexGrow: 1, minWidth: 0,
                  ...(narrow && {
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', lineHeight: 1.3,
                  }),
                }}
              >
                {flow.name}
              </Typography>
              <Box
                className="flow-card-select"
                sx={{
                  display: 'flex', lineHeight: 0, flexShrink: 0,
                  opacity: selected ? 1 : 0, transition: 'opacity 0.15s ease',
                }}
              >
                <Checkbox
                  checked={selected}
                  onChange={() => onToggleSelect(flow.id)}
                  size="small"
                  sx={{ p: 0.25 }}
                  inputProps={{ 'aria-label': `Select ${flow.name}` }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
              <Tooltip title={statusInfo.description || ''}>
                <Chip
                  label={statusInfo.label}
                  color={statusInfo.color}
                  variant={statusInfo.variant}
                  icon={statusInfo.icon}
                  size="small"
                  sx={{ flexShrink: 0 }}
                />
              </Tooltip>
              {(() => {
                // Error count comes from the flow's persisted Meta validation, no Meta call on
                // view. "Invalid with 0 listed" still means at least one error.
                const errorCount = getFlowErrorCount(flow);
                const invalid = isFlowInvalid(flow);
                if (!invalid && errorCount === 0) return null;
                const shown = errorCount || (invalid ? 1 : 0);
                const messages = getFlowErrorMessages(flow);
                const tip = messages.length
                  ? messages.join('\n')
                  : 'WhatsApp reported validation errors for this flow.';
                return (
                  <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
                    <Chip
                      label={`${shown} ${shown === 1 ? 'error' : 'errors'}`}
                      color="error"
                      variant="outlined"
                      icon={<ErrorOutlineIcon />}
                      size="small"
                      sx={{ flexShrink: 0 }}
                    />
                  </Tooltip>
                );
              })()}
              {/* A booking flow is a dynamic flow, but saying "Dynamic" to its owner
                  explains nothing. Read off the flow itself rather than off endpointUri,
                  which is only filled in once it has been deployed. */}
              {isBookingFlowJson(flow.flowJson)
                ? <LiveBookingChip />
                : flow.endpointUri && <DynamicFlowChip />}
              {(triggers || []).map((t) => {
                const isIntent = t.matchType === 'INTENT';
                const label = isIntent
                  ? `Customer asks about: “${t.intentDescription}”`
                  : `${SOURCE_LABELS[t.source] || t.source}: “${t.triggerPhrase}”`;
                const tooltip = isIntent
                  ? `Auto-sends when a customer's message seems to be about “${t.intentDescription}”${t.enabled ? '' : ' (disabled)'}`
                  : `Auto-sends when ${t.source === 'CUSTOMER' ? 'a customer' : 'you'} say${t.source === 'CUSTOMER' ? 's' : ''} “${t.triggerPhrase}”${t.enabled ? '' : ' (disabled)'}`;
                return (
                  <Tooltip key={t.id} title={tooltip}>
                    <Chip
                      label={label}
                      icon={isIntent ? <AutoAwesomeIcon /> : <BoltIcon />}
                      size="small"
                      variant="outlined"
                      color={t.enabled ? (isIntent ? 'secondary' : 'info') : 'default'}
                      sx={{ flexShrink: 0, opacity: t.enabled ? 1 : 0.6 }}
                      data-testid={`flow-trigger-chip-${t.id}`}
                    />
                  </Tooltip>
                );
              })}
            </Box>

            {flow.description && (
              <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.75 }}>
                {flow.description}
              </Typography>
            )}

            <Typography
              variant="caption" color="text.secondary" sx={{ display: 'block' }}
              data-testid={`flow-meta-${flow.id}`}
            >
              {stats.screens} screen{stats.screens === 1 ? '' : 's'} · {stats.components} component{stats.components === 1 ? '' : 's'} · Updated {formattedDate.updated}
            </Typography>

            <FlowDestinations flow={flow} targets={destinationsFor(flow.id)} />
          </CardContent>

          <FlowCardActions
            flow={flow}
            can={can}
            onEdit={handleEditFlow}
            onDuplicate={handleDuplicateFlow}
            onMove={otherEnvironments.length > 0
              ? (f) => setMoveDialog({ open: true, flow: f, target: otherEnvironments[0]?.id || '' })
              : undefined}
            onPublish={handlePublishFlow}
            onDeprecate={(flow) => setDeprecateDialog({ open: true, flow })}
            onDelete={(flow) => setDeleteDialog({ open: true, flow })}
            onDeliverAnswers={isDemoMode ? undefined : (flow) => setDeliveryDialogFlow(flow)}
            onConnections={(flow) => setConnectionsFlow(flow)}
          />
        </Card>
      </Grid>
    );
  }, (prevProps, nextProps) => {
    // Only re-render if flow data or selection has changed
    return prevProps.flow.id === nextProps.flow.id &&
           prevProps.flow.updatedAt === nextProps.flow.updatedAt &&
           prevProps.flow.status === nextProps.flow.status &&
           prevProps.selected === nextProps.selected &&
           prevProps.triggers === nextProps.triggers;
  });


  const liveFlowCount = flows.filter((flow) => flow.status === 'PUBLISHED').length;

  // Welcome-chips hint (item 3 of docs/launch/ws-welcome-chips.md): once a number is connected
  // and something is actually live to send, nudge toward setting up chips, but only if none
  // are configured yet. One light GET, not on every render: fetched once per environment once
  // the pre-conditions are true, never a write.
  const [chipsConfigured, setChipsConfigured] = useState(null); // null = unknown yet
  useEffect(() => {
    if (!isConnected || !selectedEnvironment?.id || liveFlowCount === 0) return;
    let cancelled = false;
    welcomeChipsService.get(selectedEnvironment.id, authenticatedFetch)
      .then((result) => {
        if (!cancelled) setChipsConfigured((result?.chips || []).length > 0);
      })
      .catch(() => { /* unknown stays unknown, never show a hint we can't back up */ });
    return () => { cancelled = true; };
    // authenticatedFetch is recreated every AuthContext render - depending on it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, selectedEnvironment?.id, liveFlowCount > 0]);



  return (
    <Box sx={{ height: '100vh', bgcolor: 'background.default', overflow: 'auto' }}>
      {/* Header with import controls */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar sx={{ gap: 1.25, flexWrap: 'wrap', py: 1 }}>
          <Typography variant="h6" component="h1" noWrap sx={{ flexGrow: 1, fontWeight: 650, fontSize: 18, letterSpacing: '-0.015em' }}>
            {flows.length > 0 ? `Flows (${flows.length})` : 'Flows'}
          </Typography>

          {/* Five buttons wrap into five rows on a phone and push the flows themselves below
              the fold. The + button already covers the one that matters; the rest fold into a
              menu, which is where a second-choice action belongs on a small screen. */}
          {narrow ? (
            <FlowsToolbarMenu />
          ) : (
            <>
              {can.modifyFlow && (
                <>
                  {/* The AI is the product: describing a flow is the one way in up here, beside
                      Import. Templates and starting from scratch are offered where there is room
                      to explain them (the empty state), and welcome chips live in Settings. */}
                  <Button
                    variant="contained"
                    onClick={() => navigate('/flow-builder?new=1')}
                    disabled={isLoading}
                    startIcon={<AutoAwesomeIcon />}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    Describe your flow
                  </Button>
                </>
              )}

              {/* Import button */}
              {can.importFlow && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setImportDialogOpen(true)}
                  disabled={isLoading}
                  startIcon={<ImportIcon />}
                  sx={{
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    color: 'text.secondary',
                    borderColor: 'divider',
                    bgcolor: '#fff',
                    '&:hover': { borderColor: 'text.primary', bgcolor: '#fff' },
                  }}
                >
                  Import
                </Button>
              )}

            </>
          )}
        </Toolbar>

        {/* Loading indicator */}
        {isLoading && <LinearProgress />}
      </AppBar>

      {/* Shown only once the limit is actually spent. A quota counter on an account with one
          flow is noise; the same line when the next publish will be refused is the warning
          that stops someone hitting a 402 mid-demo. */}
      {isFree && limits.liveFlows != null && liveFlowCount >= limits.liveFlows && (
        <Alert
          severity="info"
          sx={{ mx: 2, mt: 2 }}
          action={(
            <Button color="inherit" size="small" onClick={() => navigate('/settings?tab=billing')}>
              See Team
            </Button>
          )}
        >
          Free plan: {limits.liveFlows} live flow. To publish another, deprecate the one you have
         , WhatsApp makes that permanent, or upgrade for unlimited.
        </Alert>
      )}

      {/* Shown once, after the first flow goes live, until chips are actually set up, never
          writes to Meta on its own, just points at the card where a tap does that. */}
      {isConnected && liveFlowCount > 0 && chipsConfigured === false && (
        <Alert
          severity="info"
          sx={{ mx: 2, mt: 2 }}
          onClose={() => setChipsConfigured(true)}
          action={(
            <Button color="inherit" size="small" onClick={() => navigate('/settings?section=number')}>
              Set up
            </Button>
          )}
        >
          Show customers a &ldquo;Get a quote&rdquo; chip when they open your chat.
        </Alert>
      )}

      {/* Error alerts */}
      {error && (
        <Alert severity="error" onClose={() => {}} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* pb clears the floating + button, which otherwise sits on the last card's buttons. */}
      <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 12, sm: 3 } }}>
        {/* Flows view */}
        {flows.length === 0 ? (
          // First-run: the describe box is the whole point of landing here, so the CTA to it
          // leads, no marketing paragraph, one sentence, then the same secondary ways in as
          // the toolbar. Connecting a number is deliberately NOT offered here: it only matters
          // once there is a flow to send, so it shows up as its own card below after that.
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <AutoAwesomeIcon color="primary" sx={{ fontSize: 40, mb: 1.5 }} />
            <Typography variant="h6" gutterBottom>
              What should your flow do?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Describe it, and it&rsquo;s ready to send.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => navigate('/flow-builder?new=1')}
              >
                Describe your flow
              </Button>
              <Button
                variant="outlined"
                onClick={() => setTemplateDialogOpen(true)}
              >
                Start from a template
              </Button>
              <Button
                variant="text"
                color="inherit"
                sx={{ color: 'text.secondary' }}
                onClick={() => navigate('/flow-builder?new=1&mode=manual')}
              >
                Start from scratch
              </Button>
              {can.importFlow && (
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ color: 'text.secondary' }}
                  onClick={() => setImportDialogOpen(true)}
                >
                  Import
                </Button>
              )}
            </Box>
          </Paper>
        ) : (
          <>
            {/* Connecting a number matters once there is something to send, shown here rather
                than before the first flow exists, so building never has to wait on it. */}
            {!isConnected && (
              <Paper
                variant="outlined"
                data-testid="connect-waba-card"
                sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 3, flexWrap: 'wrap' }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Connect the WhatsApp number you already use
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    So your flows can be sent and go live for customers.
                  </Typography>
                </Box>
                <Button variant="contained" onClick={() => setConnectOpen(true)}>
                  Connect WhatsApp
                </Button>
              </Paper>
            )}
            <TextField
              placeholder="Search flows by name or description…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 3, maxWidth: 420, '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {selectedIds.length > 0 && (
              <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedIds.length} selected</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" onClick={clearSelection}>Clear</Button>
                <Button size="small" color="error" variant="contained" startIcon={<DeleteIcon />} onClick={() => setBulkDeleteOpen(true)}>
                  Delete {selectedIds.length}
                </Button>
              </Paper>
            )}
            {filteredFlows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No flows match &ldquo;{searchQuery}&rdquo;.
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {filteredFlows.map(flow => (
                  <FlowCard
                    key={flow.id}
                    flow={flow}
                    selected={selectedIds.includes(flow.id)}
                    onToggleSelect={toggleSelect}
                    triggers={triggersByFlow[flow.id]}
                  />
                ))}
              </Grid>
            )}
          </>
        )}
      </Box>

      {/* Individual Flow Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, flow: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete Flow
        </DialogTitle>
        <DialogContent>
          {deleteDialog.flow && (
            <>
              <Typography gutterBottom>
                Are you sure you want to delete "{deleteDialog.flow.name}"?
              </Typography>

              {deleteDialog.flow.whatsappFlowId && (
                deleteDialog.flow.status === 'DEPRECATED'
                  || ['PUBLISHED', 'DEPRECATED'].includes(deleteDialog.flow.whatsappStatus) ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    This removes the flow from FlowGate only. WhatsApp keeps published flows
                    permanently, so it will remain in your WhatsApp Business Account as Deprecated.
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This flow will also be deleted from your WhatsApp Business Account.
                    This action cannot be undone.
                  </Alert>
                )
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, flow: null })}>
            Cancel
          </Button>

          <Button
            onClick={() => handleDeleteFlow(deleteDialog.flow)}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Delete Flow'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Flow Dialog */}
      <Dialog open={moveDialog.open} onClose={() => setMoveDialog({ open: false, flow: null, target: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Move &ldquo;{moveDialog.flow?.name}&rdquo;</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move this flow to another environment in this organisation. It returns to a draft and
            re-syncs to the destination&rsquo;s WhatsApp account.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="move-target-label">Destination environment</InputLabel>
            <Select
              labelId="move-target-label"
              label="Destination environment"
              value={moveDialog.target}
              onChange={(e) => setMoveDialog((prev) => ({ ...prev, target: e.target.value }))}
            >
              {otherEnvironments.map((env) => (
                <MenuItem key={env.id} value={env.id}>{env.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialog({ open: false, flow: null, target: '' })}>Cancel</Button>
          <Button onClick={handleMoveFlow} variant="contained" disabled={!moveDialog.target || isLoading}>
            {isLoading ? <CircularProgress size={20} /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {selectedIds.length} flow{selectedIds.length === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This permanently removes the selected flows. Published flows are skipped, deprecate them first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkDelete} color="error" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} /> : `Delete ${selectedIds.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit-published warning: editing a live flow unpublishes it on WhatsApp */}
      <Dialog
        open={editPublishedDialog.open}
        onClose={() => setEditPublishedDialog({ open: false, flow: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit a published flow?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            "{editPublishedDialog.flow?.name}" is live on WhatsApp.
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            WhatsApp doesn't allow changing a published flow in place, editing it will
            <strong> unpublish it</strong> (it returns to draft), and you'll need to publish again to
            make your changes live. Edit a copy instead to keep the live flow published.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setEditPublishedDialog({ open: false, flow: null })}>
            Cancel
          </Button>
          <Button onClick={handleEditPublishedAnyway} color="warning">
            Edit anyway
          </Button>
          <Button onClick={handleEditPublishedAsCopy} variant="contained" startIcon={<DuplicateIcon />}>
            Edit a copy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deprecate Flow Dialog */}
      <Dialog
        open={deprecateDialog.open}
        onClose={() => setDeprecateDialog({ open: false, flow: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Deprecate Flow
        </DialogTitle>
        <DialogContent>
          {deprecateDialog.flow && (
            <>
              <Typography gutterBottom>
                Are you sure you want to deprecate "{deprecateDialog.flow.name}"?
              </Typography>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>This cannot be undone.</strong> WhatsApp keeps a deprecated flow
                forever: it can never be sent or published again, and deleting it here only
                removes it from your list. Customers already part-way through it can finish.
                To change a flow and keep sending it, edit it instead, or duplicate it and
                deprecate this one once the copy is live.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeprecateDialog({ open: false, flow: null })}>
            Cancel
          </Button>

          <Button
            onClick={() => handleDeprecateFlow(deprecateDialog.flow)}
            color="warning"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Deprecate Flow'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Flows Dialog */}
      <ImportFlowsDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
      />

      {/* Template Gallery */}
      <TemplateGalleryDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onPick={handlePickTemplate}
      />

      {/* Delivery destinations for one flow ("Deliver answers…" in the card menu) */}
      <DestinationDialog
        open={!!deliveryDialogFlow}
        onClose={() => {
          setDeliveryDialogFlow(null);
          // Whatever was added or removed in there is what the cards now claim.
          setDestinationsRefresh((n) => n + 1);
        }}
        flow={deliveryDialogFlow}
      />

      {/* Everything one flow is connected to: keywords, live data, where answers go */}
      <FlowConnectionsDrawer
        open={!!connectionsFlow}
        flow={connectionsFlow}
        onClose={() => setConnectionsFlow(null)}
        onChanged={() => {
          setDestinationsRefresh((n) => n + 1);
          setTriggersRefresh((n) => n + 1);
        }}
      />

      {/* Connect the WhatsApp number you already use (the card shown once a flow exists) */}
      <ConnectWabaDialog
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        environment={selectedEnvironment}
      />

      {/* Toast notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Create New Flow FAB */}
      <Fab
        color="primary"
        aria-label="create new flow"
        onClick={() => navigate('/flow-builder?new=1')}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default FlowsPage;
